import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, MapPin, Loader2 } from "lucide-react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import Seo from "../components/Seo";
import { formatApiErrorDetail } from "../lib/helpers";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !emailRe.test(form.email.trim()) || !form.message.trim()) {
      toast.error("Please enter your name, a valid email, and a message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send your message.");
    } finally {
      setSending(false);
    }
  };

  const input = "w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-[#0A0A0A] focus:border-[#0A0A0A] focus:ring-2 focus:ring-[#0A0A0A]/30 outline-none transition-all duration-200";

  return (
    <div className="bg-white" data-testid="contact-page">
      <Seo title="Contact MAS Grading" description="Questions about grading, submissions, or your order? Contact the MAS Grading team — we're here to help." path="/contact" />
      <PageHeader eyebrow="Get in touch" title="Contact MAS Grading" subtitle="Questions about grading, submissions, or your order? We're here to help." />
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-14">
        {/* Form */}
        <form onSubmit={submit} className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-sm" data-testid="contact-form">
          <h2 className="font-display font-extrabold text-2xl text-[#0A0A0A] mb-6">Send us a message</h2>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Name *</label>
              <input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={`mt-2 ${input}`} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Email *</label>
              <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`mt-2 ${input}`} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Subject</label>
              <input data-testid="contact-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className={`mt-2 ${input}`} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">Message *</label>
              <textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={`mt-2 ${input}`} />
            </div>
          </div>
          <button data-testid="contact-submit" type="submit" disabled={sending} className="mt-6 inline-flex items-center gap-2 px-8 py-4 bg-[#0A0A0A] text-white font-bold rounded-full hover:bg-neutral-800 transition-colors duration-300 disabled:opacity-50">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {sending ? "Sending…" : "Send message"}
          </button>
        </form>

        {/* Details */}
        <div>
          <div className="rounded-3xl bg-[#0A0A0A] text-white p-8">
            <div className="flex items-center gap-2 text-white/50 text-xs font-bold uppercase tracking-[0.2em]"><Mail size={15} /> Email</div>
            <a href="mailto:support@mascertified.com" className="font-display font-bold text-xl mt-2 inline-block hover:text-[#4DB8E8] transition-colors" data-testid="contact-support-email">support@mascertified.com</a>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 mt-5">
            <div className="rounded-3xl border border-neutral-200 bg-[#FAFAFA] p-7">
              <div className="flex items-center gap-2 text-neutral-400 text-xs font-bold uppercase tracking-[0.2em]"><MapPin size={15} /> Mail-in (USPS)</div>
              <div className="mt-3 text-[#0A0A0A] leading-relaxed">
                <div className="font-bold">MAS Certified, Inc.</div>
                <div>PO Box 3153</div>
                <div>McAllen, TX 78502</div>
              </div>
            </div>
            <div className="rounded-3xl border border-neutral-200 bg-[#FAFAFA] p-7">
              <div className="flex items-center gap-2 text-neutral-400 text-xs font-bold uppercase tracking-[0.2em]"><MapPin size={15} /> UPS / FedEx</div>
              <div className="mt-3 text-[#0A0A0A] leading-relaxed">
                <div className="font-bold">MAS Certified, Inc.</div>
                <div>620 E Pecan Blvd #3153</div>
                <div>McAllen, TX 78501</div>
              </div>
            </div>
          </div>
          <p className="text-neutral-500 text-sm mt-6 leading-relaxed">Please include your submission number (if you have one) so we can help you faster. For grading status, sign in and visit your portal.</p>
        </div>
      </section>
    </div>
  );
}
